// ============================================================================
// ProcessingPipeline — Visual batch job status tracker
// Shows 5 processing stages with live status updates
// ============================================================================

import { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import type { JobStatus, JobType, JobStatusType } from '../../types';
import { PIPELINE_JOB_ORDER, JOB_TYPE_INFO } from '../../types';

interface ProcessingPipelineProps {
  jobs: JobStatus[];
  loading?: boolean;
  clipStatus?: string;
  transcriptApprovalStatus?: string;
  onRetryJob: (jobType: JobType) => Promise<void>;
}

// Build a complete pipeline state from whatever jobs exist in DynamoDB
function buildPipelineState(
  jobs: JobStatus[],
  {
    clipStatus,
    transcriptApprovalStatus,
  }: {
    clipStatus?: string;
    transcriptApprovalStatus?: string;
  } = {},
): Map<JobType, JobStatus> {
  const jobMap = new Map<JobType, JobStatus>();

  // Pre-fill all pipeline stages as pending
  for (const jobType of PIPELINE_JOB_ORDER) {
    jobMap.set(jobType, {
      contentId: jobs[0]?.contentId || '',
      jobType,
      status: 'pending',
    });
  }

  // Overlay actual job data
  for (const job of jobs) {
    if (PIPELINE_JOB_ORDER.includes(job.jobType)) {
      jobMap.set(job.jobType, {
        ...job,
        error: (job as JobStatus & { errorMessage?: string }).error || (job as JobStatus & { errorMessage?: string }).errorMessage,
      });
    }
  }

  const awaitingTranscriptApproval =
    clipStatus === 'base_clips_ready' && transcriptApprovalStatus !== 'approved';

  if (awaitingTranscriptApproval) {
    const captionJob = jobMap.get('caption_burnin');
    const draftsJob = jobMap.get('generate_drafts');

    if (!captionJob || captionJob.status === 'pending' || captionJob.status === 'failed') {
      jobMap.set('caption_burnin', {
        contentId: jobs[0]?.contentId || '',
        jobType: 'caption_burnin',
        status: 'pending',
      });
    }

    if (!draftsJob || draftsJob.status === 'pending' || draftsJob.status === 'failed') {
      jobMap.set('generate_drafts', {
        contentId: jobs[0]?.contentId || '',
        jobType: 'generate_drafts',
        status: 'pending',
      });
    }
  }

  return jobMap;
}

function formatElapsed(startedAt: string): string {
  const elapsed = Date.now() - new Date(startedAt).getTime();
  const seconds = Math.floor(elapsed / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSec = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSec}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMin = minutes % 60;
  return `${hours}h ${remainingMin}m`;
}

function formatDuration(startedAt: string, completedAt: string): string {
  const elapsed = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  const seconds = Math.floor(elapsed / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSec = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSec}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMin = minutes % 60;
  return `${hours}h ${remainingMin}m`;
}

// ── Status Indicator Component ──────────────────────────────────────────

function StatusIndicator({ status }: { status: JobStatusType }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-5 w-5 text-[#004721]" />;
    case 'running':
      return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
    case 'queued':
      return <Clock className="h-5 w-5 text-amber-500" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'pending':
    default:
      return (
        <div className="h-5 w-5 rounded-full border-2 border-gray-300 bg-gray-100" />
      );
  }
}

function statusLabel(status: JobStatusType): string {
  switch (status) {
    case 'completed': return 'Complete';
    case 'running': return 'Processing...';
    case 'queued': return 'Queued';
    case 'failed': return 'Failed';
    case 'pending':
    default: return 'Pending';
  }
}

function statusTextColor(status: JobStatusType): string {
  switch (status) {
    case 'completed': return 'text-[#004721]';
    case 'running': return 'text-blue-600';
    case 'queued': return 'text-amber-600';
    case 'failed': return 'text-red-600';
    case 'pending':
    default: return 'text-gray-400';
  }
}

function connectorColor(prevStatus: JobStatusType): string {
  if (prevStatus === 'completed') return 'text-[#004721]';
  return 'text-gray-300';
}

// ── Pipeline Step Component ─────────────────────────────────────────────

function PipelineStep({ job, isLast: _ }: { job: JobStatus; isLast: boolean }) {
  const [errorExpanded, setErrorExpanded] = useState(false);
  const info = JOB_TYPE_INFO[job.jobType];

  return (
    <div className="flex flex-col items-center">
      {/* Step card */}
      <div
        className={`
          relative flex flex-col items-center rounded-xl border px-4 py-3 transition-all duration-300
          ${job.status === 'running' ? 'border-blue-200 bg-blue-50/50 shadow-sm shadow-blue-100' : ''}
          ${job.status === 'completed' ? 'border-[#004721]/20 bg-[#004721]/5' : ''}
          ${job.status === 'failed' ? 'border-red-200 bg-red-50/50' : ''}
          ${job.status === 'queued' ? 'border-amber-200 bg-amber-50/30' : ''}
          ${job.status === 'pending' ? 'border-gray-200 bg-gray-50/50' : ''}
        `}
      >
        <StatusIndicator status={job.status} />

        <p className="mt-2 text-xs font-semibold text-gray-900 text-center leading-tight">
          {info.label}
        </p>

        <p className={`mt-0.5 text-[10px] font-medium ${statusTextColor(job.status)}`}>
          {statusLabel(job.status)}
        </p>

        {/* Elapsed time for running jobs */}
        {job.status === 'running' && job.startedAt && (
          <p className="mt-0.5 text-[10px] text-blue-500 font-mono">
            {formatElapsed(job.startedAt)}
          </p>
        )}

        {/* Duration for completed jobs */}
        {job.status === 'completed' && job.startedAt && job.completedAt && (
          <p className="mt-0.5 text-[10px] text-[#004721]/70 font-mono">
            {formatDuration(job.startedAt, job.completedAt)}
          </p>
        )}
      </div>

      {/* Error expandable for failed jobs */}
      {job.status === 'failed' && job.error && (
        <div className="mt-1.5 w-full max-w-[160px]">
          <button
            onClick={() => setErrorExpanded(!errorExpanded)}
            className="flex w-full items-center justify-center gap-1 text-[10px] text-red-500 hover:text-red-700 transition-colors"
          >
            {errorExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {errorExpanded ? 'Hide error' : 'Show error'}
          </button>
          {errorExpanded && (
            <div className="mt-1 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-[10px] text-red-700 break-words">
              {job.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Mobile Pipeline Step ────────────────────────────────────────────────

function MobilePipelineStep({ job, isLast }: { job: JobStatus; isLast: boolean }) {
  const [errorExpanded, setErrorExpanded] = useState(false);
  const info = JOB_TYPE_INFO[job.jobType];

  return (
    <>
      <div className="flex items-start gap-3">
        {/* Left: status icon + vertical connector */}
        <div className="flex flex-col items-center">
          <StatusIndicator status={job.status} />
          {!isLast && (
            <div
              className={`
                mt-1 h-8 w-0.5 transition-colors duration-300
                ${job.status === 'completed' ? 'bg-[#004721]/30' : 'bg-gray-200'}
              `}
            />
          )}
        </div>

        {/* Right: details */}
        <div className="flex-1 pb-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900">{info.label}</p>
            <span className={`text-xs font-medium ${statusTextColor(job.status)}`}>
              {statusLabel(job.status)}
            </span>
          </div>

          {job.status === 'running' && job.startedAt && (
            <p className="text-xs text-blue-500 font-mono mt-0.5">
              Elapsed: {formatElapsed(job.startedAt)}
            </p>
          )}

          {job.status === 'completed' && job.startedAt && job.completedAt && (
            <p className="text-xs text-[#004721]/70 font-mono mt-0.5">
              Duration: {formatDuration(job.startedAt, job.completedAt)}
            </p>
          )}

          {job.status === 'failed' && job.error && (
            <div className="mt-1">
              <button
                onClick={() => setErrorExpanded(!errorExpanded)}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors"
              >
                {errorExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {errorExpanded ? 'Hide error' : 'View error'}
              </button>
              {errorExpanded && (
                <div className="mt-1 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
                  {job.error}
                  {job.completedAt && (
                    <p className="mt-1 text-red-400 text-[10px]">
                      Failed at: {new Date(job.completedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Failed Jobs Summary ─────────────────────────────────────────────────

function FailedJobsSummary({ jobs, onRetryJob }: { jobs: JobStatus[]; onRetryJob: (jobType: JobType) => Promise<void> }) {
  const failedJobs = jobs.filter((j) => j.status === 'failed');
  const [expanded, setExpanded] = useState(false);
  const [retryingJob, setRetryingJob] = useState<JobType | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  if (failedJobs.length === 0) return null;

  const handleRetry = async (jobType: JobType) => {
    setRetryError(null);
    setRetryingJob(jobType);
    try {
      await onRetryJob(jobType);
      setExpanded(false);
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Failed to retry job');
    } finally {
      setRetryingJob(null);
    }
  };

  return (
    <div className="mt-4 w-fit rounded-xl border border-red-200 bg-red-50/50">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <span className="text-sm font-medium text-red-700">
            {failedJobs.length} job{failedJobs.length > 1 ? 's' : ''} failed
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-red-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-red-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-red-100 px-4 py-3 space-y-3">
          {retryError && (
            <div className="rounded-lg border border-red-200 bg-red-100/70 px-3 py-2 text-xs text-red-700">
              {retryError}
            </div>
          )}
          {failedJobs.map((job) => {
            const info = JOB_TYPE_INFO[job.jobType];
            return (
              <div key={job.jobType} className="space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-red-800">{info.label}</p>
                  <button
                    onClick={() => void handleRetry(job.jobType)}
                    disabled={retryingJob !== null}
                    className="flex items-center gap-1 rounded-lg bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700 transition hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-60"
                    title={`Retry ${info.label}`}
                  >
                    <RotateCcw className={`h-3 w-3 ${retryingJob === job.jobType ? 'animate-spin' : ''}`} />
                    {retryingJob === job.jobType ? 'Retrying…' : 'Retry'}
                  </button>
                </div>
                {job.error && (
                  <p className="text-xs text-red-600 bg-red-100/50 rounded-lg px-3 py-2">
                    {job.error}
                  </p>
                )}
                {job.completedAt && (
                  <p className="text-[10px] text-red-400">
                    Failed at: {new Date(job.completedAt).toLocaleString()}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────

export function ProcessingPipeline({ jobs, loading, clipStatus, transcriptApprovalStatus, onRetryJob }: ProcessingPipelineProps) {
  const pipelineState = buildPipelineState(jobs, { clipStatus, transcriptApprovalStatus });
  const orderedJobs = PIPELINE_JOB_ORDER.map((jt) => pipelineState.get(jt)!);

  // Overall progress
  const completedCount = orderedJobs.filter((j) => j.status === 'completed').length;
  const totalCount = PIPELINE_JOB_ORDER.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  const hasAnyActive = orderedJobs.some((j) => j.status === 'running' || j.status === 'queued');
  const allDone = orderedJobs.every((j) => j.status === 'completed' || j.status === 'failed');

  if (loading) {
    return (
      <div className="card">
        <div className="px-6 py-4">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            <span className="text-sm text-gray-500">Loading pipeline status...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-100 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Processing Pipeline</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {allDone
                ? `Processing complete — ${completedCount}/${totalCount} succeeded`
                : hasAnyActive
                  ? `Processing... ${completedCount}/${totalCount} complete`
                  : `${completedCount}/${totalCount} stages complete`
              }
            </p>
          </div>
          {hasAnyActive && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
              </span>
              Live
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`
              h-full rounded-full transition-all duration-700 ease-out
              ${allDone && completedCount === totalCount ? 'bg-[#004721]' : 'bg-blue-500'}
            `}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Desktop: Horizontal pipeline */}
      <div className="hidden md:block px-6 py-5">
        <div className="flex items-center justify-between gap-1">
          {orderedJobs.map((job, idx) => (
            <div key={job.jobType} className="flex items-center flex-1">
              <PipelineStep job={job} isLast={idx === orderedJobs.length - 1} />
              {idx < orderedJobs.length - 1 && (
                <div className={`flex items-center justify-center px-1 ${connectorColor(job.status)}`}>
                  <ArrowRight className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: Vertical pipeline */}
      <div className="block md:hidden px-6 py-4">
        {orderedJobs.map((job, idx) => (
          <MobilePipelineStep
            key={job.jobType}
            job={job}
            isLast={idx === orderedJobs.length - 1}
          />
        ))}
      </div>

      {/* Failed jobs summary */}
      <div className="px-6 pb-4">
        <FailedJobsSummary jobs={orderedJobs} onRetryJob={onRetryJob} />
      </div>
    </div>
  );
}
